import deepmerge from "deepmerge";

import { Theme } from "@emotion/react";
import {
  AnyProgressBarWidgetTheme,
  DiscountCartWidgetStyles,
  DiscountMessageBoxWidgetTheme,
  DiscountQuantityWidgetTheme,
  IgShippingProgressBarTheme,
  WidgetDesktopMobileTheme,
  WidgetPrefix,
  WidgetPrefixes,
} from "@intelligems/ig-types";
import { Css, overwriteMerge } from "@intelligems/ig-utils";

declare module "@emotion/react" {
  export interface Theme {
    igShippingProgressBar: WidgetDesktopMobileTheme<IgShippingProgressBarTheme>;
    quantityButtonsWidget1: WidgetDesktopMobileTheme<DiscountQuantityWidgetTheme>;
    quantityButtonsWidget4: WidgetDesktopMobileTheme<DiscountQuantityWidgetTheme>;
    progressBarWidget1: WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
    progressBarWidget2?: WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
    progressBarWidget3?: WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
    progressBarWidget4?: WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
    progressBarWidget5?: WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
    // cartWidget1?: WidgetDesktopMobileTheme<DiscountCartWidgetStyles>;
    messageBoxPopupWidget1: WidgetDesktopMobileTheme<DiscountMessageBoxWidgetTheme>;
    messageBoxPopupWidget2: WidgetDesktopMobileTheme<DiscountMessageBoxWidgetTheme>;
    messageBoxSlideOutWidget1: WidgetDesktopMobileTheme<DiscountMessageBoxWidgetTheme>;
    messageBoxSlideOutWidget2: WidgetDesktopMobileTheme<DiscountMessageBoxWidgetTheme>;
  }
}

type dpb = WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
type spb = WidgetDesktopMobileTheme<AnyProgressBarWidgetTheme>;
type dw = WidgetDesktopMobileTheme<DiscountQuantityWidgetTheme>;
type cw = WidgetDesktopMobileTheme<DiscountCartWidgetStyles>;
type dmb = WidgetDesktopMobileTheme<DiscountMessageBoxWidgetTheme>;

export const SHIPPING_PROGRESS_BAR_CSS_VARS = {
  maxWidth: "--igspb-max-width",
  textSize: "--igspb-text-size",
  underMinimumBackgroundColor: "--igspb-under-minimum-background-color",
  textColor: "--igspb-text-color",
  progressBarBackgroundColor: "--igspb-progress-bar-background-color",
  progressBarCompletedColor: "--igspb-progress-bar-completed-color",
  progressBarHeight: "--igspb-progress-bar-height",
};

const quantityWidgetColors = (
  prefix: string,
  isMobile: boolean
): Theme["quantityButtonsWidget1"]["desktop"]["colors"] => {
  return {
    primary: `var(${Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.primary",
      isMobile
    )}, #6c757d)`,
    secondary: `var(${Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.secondary",
      isMobile
    )}, #adb5bd)`,
    primaryKey: Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.primary",
      isMobile
    ),
    secondaryKey: Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.secondary",
      isMobile
    ),
    border: `var(${Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.border",
      isMobile
    )}, #adb5bd)`,
    messagePrimaryBackgroundColor: `var(${Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.primary",
      isMobile
    )}, #6c757d)`,
    messageSecondaryBackgroundColor: `var(${Css.getWidgetCssVariableName<dw>(
      prefix,
      "colors.secondary",
      isMobile
    )}, #adb5bd)`,
  };
};

export const FullIgTheme = (
  isMobile: boolean,
  prefixes?: WidgetPrefixes
): Theme => {
  const prefix = (widgetPrefix: WidgetPrefix): string => {
    return prefixes?.[widgetPrefix] || widgetPrefix;
  };

  const igShippingProgressBar: Theme["igShippingProgressBar"] = {
    desktop: {
      maxWidth: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.maxWidth}, 100%)`,
      textSize: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.textSize}, 12px)`,
      underMinimumBackgroundColor: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.underMinimumBackgroundColor}, #ff6666)`,
      underMinimumBackgroundColorKey:
        SHIPPING_PROGRESS_BAR_CSS_VARS.underMinimumBackgroundColor,
      textColor: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.textColor}, inherit)`,
      progressBarBackgroundColor: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.progressBarBackgroundColor}, #eeeeee)`,
      progressBarCompletedColor: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.progressBarCompletedColor}, #0013BC)`,
      progressBarHeight: `var(${SHIPPING_PROGRESS_BAR_CSS_VARS.progressBarHeight}, 10px)`,
    },
    mobile: {},
  };

  const progressBarWidget1: Theme["progressBarWidget1"] = {
    desktop: {
      container: {
        minHeight: 75,
        maxHeight: 100,
        justifyContent: "center",
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        )}, #eeeeee)`,
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        ),
        margin: "0 auto 0 auto",
        padding: "10px 30px 10px  30px",
        font: {
          family: "container.inherit",
        },
      },
      bar: {
        height: 12,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.inactiveColor",
          isMobile
        )}, #eeeeee)`,
      },
      message: {
        enabled: true,
        row: 1,
      },
      breakpoint: {
        distribution: "even",
        padding: "15px 0 15px 0",
        margin: "0 0 0 0",
        completed: "completedBreakpoint1",
        locked: "lockedBreakpoint1",
        enabled: false,
        row: 2,
        inset: 16,
        size: 28,
        height: 30,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        )}, #eeeeee)`,
        activeColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        ),
        inactiveColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        ),
      },
      label: {
        enabled: true,
        row: 3,
      },
      tooltip: {
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor"
        ),
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor",
          isMobile
        )}, #E1E1E1)`,
      },
      row1: {
        justifyContent: "flex-start",
      },
      row2: {
        justifyContent: "center",
      },
      row3: {
        justifyContent: "center",
      },
    },
    mobile: {},
  };
  const progressBarWidget2: Theme["progressBarWidget2"] = {
    desktop: {
      container: {
        minHeight: undefined,
        maxHeight: undefined,
        justifyContent: "flex-start",
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        )}, #eeeeee)`,
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        ),
        margin: "0 auto 0 auto",
        padding: "10px 30px 10px 30px",
        font: {
          family: "inherit",
        },
      },
      bar: {
        height: 12,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.inactiveColor",
          isMobile
        )}, #eeeeee)`,
      },
      message: {
        enabled: true,
        row: 1,
      },
      breakpoint: {
        distribution: "even",
        padding: "0 0 0 0",
        margin: "0 14px 0 0",
        completed: "completedBreakpoint2",
        locked: "lockedBreakpoint2",
        enabled: true,
        row: 2,
        height: 58,
        inset: 16,
        size: 28,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        )}, #eeeeee)`,
        activeColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        ),
        inactiveColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        ),
      },
      label: {
        enabled: true,
        row: 3,
      },
      tooltip: {
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor"
        ),
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor",
          isMobile
        )}, #E1E1E1)`,
      },
      row1: {
        justifyContent: "center",
      },
      row2: {
        justifyContent: "center",
      },
      row3: {
        justifyContent: "center",
      },
    },
    mobile: {},
  };
  const progressBarWidget3: Theme["progressBarWidget3"] = {
    desktop: {
      container: {
        minHeight: 75,
        maxHeight: 100,
        justifyContent: "center",
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        )}, #eeeeee)`,
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor",
          isMobile
        ),
        margin: "0 auto 0 auto",
        padding: "10px 30px 10px 30px",
        font: {
          family: "inherit",
        },
      },
      bar: {
        height: 12,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.inactiveColor",
          isMobile
        )}, #eeeeee)`,
      },
      message: {
        enabled: true,
        row: 1,
      },
      breakpoint: {
        distribution: "even",
        padding: "15px 0 15px 0",
        completed: "completedBreakpoint3",
        locked: "lockedBreakpoint3",
        margin: "0 0 0 0",
        enabled: true,
        row: 2,
        inset: 16,
        size: 16,
        height: 16,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        )}, #eeeeee)`,
        activeColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor",
          isMobile
        ),
        inactiveColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor",
          isMobile
        ),
      },
      label: {
        enabled: false,
        row: 3,
      },
      tooltip: {
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor"
        ),
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor",
          isMobile
        )}, #E1E1E1)`,
      },
      row1: {
        justifyContent: "flex-start",
      },
      row2: {
        justifyContent: "center",
      },
      row3: {
        justifyContent: "center",
      },
    },
    mobile: {},
  };

  // The only difference between progressBarWidget2 and progressBarWidget4 is breakpoint svg
  const progressBarWidget4 = deepmerge(progressBarWidget2, {
    desktop: {
      breakpoint: {
        completed: "completedBreakpoint4",
        locked: "lockedBreakpoint4",
      },
    },
  });
  const progressBarWidget5: Theme["progressBarWidget5"] = {
    desktop: {
      container: {
        minHeight: 125,
        maxHeight: 150,
        justifyContent: "flex-start",
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor"
        )}, #eeeeee)`,
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "container.backgroundColor"
        ),
        margin: "0 auto 0 auto",
        padding: "10px 30px 10px 30px",
        font: {
          family: "inherit",
        },
      },
      bar: {
        height: 5,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.activeColor"
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "bar.inactiveColor"
        )}, #eeeeee)`,
      },
      message: {
        enabled: true,
        row: 1,
      },
      breakpoint: {
        distribution: "even",
        padding: "15px 0 15px 0",
        completed: "completedBreakpoint5",
        locked: "lockedBreakpoint5",
        enabled: true,
        row: 2,
        margin: "0 0 0 0",
        inset: 16,
        size: 28,
        height: 30,
        activeColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor"
        )}, #0013BC)`,
        inactiveColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor"
        )}, #eeeeee)`,
        activeColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.activeColor"
        ),
        inactiveColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "breakpoint.inactiveColor"
        ),
      },
      label: {
        enabled: true,
        row: 3,
      },
      tooltip: {
        backgroundColorKey: Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor"
        ),
        backgroundColor: `var(${Css.getWidgetCssVariableName<dpb>(
          prefix("ig-pb"),
          "tooltip.backgroundColor"
        )}, #E1E1E1)`,
      },
      row1: {
        justifyContent: "flex-start",
      },
      row2: {
        justifyContent: "center",
      },
      row3: {
        justifyContent: "center",
      },
    },
    mobile: {},
  };

  const quantityButtonsWidget1: Theme["quantityButtonsWidget1"] = {
    desktop: {
      container: {
        margin: "20px",
        gap: "15px 10px",
        font: {
          family: "inherit",
        },
      },
      button: {
        height: "65px",
        borderRadius: "10px",
        borderThickness: "2px",
      },
      priceSection: {
        margin: "-13px 0 0 0",
      },
      extraMessage: {
        padding: "5px 0 0 0 ",
        width: "unset",
        margin: "-13px 20px 0 20px",
        borderRadius: "9px",
        minWidth: "100px",
      },
      colors: quantityWidgetColors(prefix("ig-dq"), isMobile),
    },
    mobile: {
      extraMessage: {
        minWidth: "105px",
      },
      colors: quantityWidgetColors(prefix("ig-dq"), isMobile),
    },
  };
  const quantityButtonsWidget4: Theme["quantityButtonsWidget4"] = {
    desktop: {
      container: {
        margin: `20px`,
        gap: "10px",
        font: {
          family: "inherit",
        },
      },
      button: {
        height: "80px",
        borderRadius: "0px",
        borderThickness: "2px",
      },
      priceSection: {
        margin: "0 0 0 0",
      },
      extraMessage: {
        padding: "3px 0 3px 0 ",
        margin: "0 -2px -2px -2px",
        width: "100%",
        borderRadius: "0px",
        minWidth: "135px",
      },
      colors: quantityWidgetColors(prefix("ig-dq"), isMobile),
    },
    mobile: {
      extraMessage: {
        minWidth: "105px",
      },
      colors: quantityWidgetColors(prefix("ig-dq"), isMobile),
    },
  };
  // const cartWidget1: Theme["cartWidget1"] = {
  //   desktop: {
  //     colors: {
  //       buttonColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.buttonColor"
  //       )}, #B1B2B3)`,
  //       buttonColorKey: Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.buttonColor"
  //       ),
  //       borderColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.borderColor"
  //       )}, #B1B2B3)`,
  //       textColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.textColor"
  //       )}, #7FA9A1)`,
  //     },
  //   },
  //   mobile: {
  //     colors: {
  //       buttonColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.buttonColor",
  //         true
  //       )}, #B1B2B3)`,
  //       buttonColorKey: Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.buttonColor",
  //         true
  //       ),
  //       borderColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.borderColor",
  //         true
  //       )}, #B1B2B3)`,
  //       textColor: `var(${Css.getWidgetCssVariableName<cw>(
  //         "ig-dc",
  //         "colors.textColor",
  //         true
  //       )}, #7FA9A1)`,
  //     },
  //   },
  // };
  const messageBoxPopupWidget1: Theme["messageBoxPopupWidget1"] = {
    desktop: {
      image: {
        width: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "image.width"
        )}, 80%)`,
      },
      content: {
        padding: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "content.padding",
          true
        )}, 20px 10px 20px 20px)`,
      },
      container: {
        borderTopRightRadius: "15px",
        borderBottomRightRadius: "15px",
        borderBottomLeftRadius: "15px",
        borderTopLeftRadius: "15px",
        padding: "10px",
        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        textColor: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.textColor"
        )}, white)`,
        backgroundColor: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.backgroundColor"
        )}, #6c757d)`,
        display: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.display"
        )}, flex)`,
        titleFontSize: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.titleFontSize"
        )}, 18px)`,
        bodyFontSize: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.bodyFontSize"
        )}, 14px)`,
        titleFontWeight: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.titleFontWeight"
        )}, 700)`,
        bodyFontWeight: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.bodyFontWeight"
        )}, 500)`,
        zIndex: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.zIndex"
        )}, 998)`,
        height: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.height",
          true
        )}, 113px)`,
        width: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.width"
        )}, 450px)`,
        top: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.top"
        )}, 20%)`,
        left: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.left"
        )}, 5%)`,
      },
    },
    mobile: {
      image: {
        width: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "image.width",
          true
        )}, 80%)`,
      },
      content: {
        padding: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "content.padding",
          true
        )}, 20px 10px 20px 20px)`,
      },
      container: {
        textColor: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.textColor",
          true
        )}, white)`,
        backgroundColor: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.backgroundColor",
          true
        )}, #6c757d)`,
        display: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.display"
        )}, flex)`,
        titleFontSize: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.titleFontSize"
        )}, 18px)`,
        bodyFontSize: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.bodyFontSize"
        )}, 14px)`,
        titleFontWeight: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.titleFontWeight"
        )}, 700)`,
        bodyFontWeight: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.bodyFontWeight"
        )}, 500)`,
        zIndex: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.zIndex",
          true
        )}, 998)`,
        width: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.width",
          true
        )}, 450px)`,
        height: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.height",
          true
        )}, 113px)`,
        top: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.top",
          true
        )}, 20%)`,
        left: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.left",
          true
        )}, 5%)`,
      },
    },
  };

  // The only difference between messageBoxPopupWidget1 and messageBoxPopupWidget2 is the border radius
  const messageBoxPopupWidget2 = deepmerge(messageBoxPopupWidget1, {
    desktop: {
      container: {
        borderTopLeftRadius: "0px",
        borderBottomLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomRightRadius: "0px",
      },
    },
  });

  // The only difference between messageBoxPopupWidget1 and messageBoxSlideOutWidget1 is the left border radii and left positioning
  const messageBoxSlideOutWidget1 = deepmerge(messageBoxPopupWidget1, {
    desktop: {
      container: {
        borderTopLeftRadius: "0px",
        borderBottomLeftRadius: "0px",
        left: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.left"
        )}, 0)`,
      },
    },
    mobile: {
      container: {
        left: `var(${Css.getWidgetCssVariableName<dmb>(
          prefix(`ig-mb`),
          "container.left",
          true
        )}, 0)`,
      },
    },
  });

  // The only difference between messageBoxSlideOutWidget2 and messageBoxSlideOutWidget1 is the border radius
  const messageBoxSlideOutWidget2 = deepmerge(messageBoxSlideOutWidget1, {
    desktop: {
      container: {
        borderTopLeftRadius: "0px",
        borderBottomLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomRightRadius: "0px",
      },
    },
  });

  return {
    igShippingProgressBar,
    progressBarWidget1,
    progressBarWidget2,
    progressBarWidget3,
    progressBarWidget4,
    progressBarWidget5,
    quantityButtonsWidget1,
    quantityButtonsWidget4,
    // cartWidget1,
    messageBoxPopupWidget1: messageBoxPopupWidget1,
    messageBoxPopupWidget2: messageBoxPopupWidget2,
    messageBoxSlideOutWidget1,
    messageBoxSlideOutWidget2,
  };
};

export const IgWidgetTheme = (
  widget: keyof Theme,
  isMobile: boolean,
  prefixes?: WidgetPrefixes
) => {
  if (isMobile) {
    return deepmerge(
      // @ts-ignore
      FullIgTheme(isMobile, prefixes)[widget]?.desktop || {},
      // @ts-ignore
      FullIgTheme(isMobile, prefixes)[widget]?.mobile || {},
      {
        arrayMerge: overwriteMerge,
      }
    );
  }
  return FullIgTheme(isMobile, prefixes)[widget]?.desktop || {};
};
